import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Spinner, Alert, Form, Button, Nav, Tab } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { MEMBER_TERM } from './config/clubTerms';

const MagazineIntegration = () => {
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  // 獲取所有社別網站列表
  const fetchWebsites = async () => {
    console.log('🔄 開始載入社別網站列表...');
    setLoading(true);
    try {
      console.log('📡 發送 API 請求:', '/magazines/websites');
      const response = await axios.get('/magazines/websites');
      console.log('✅ API 回應成功:', response.data);
      setWebsites(response.data.websites);
      setError(null);
    } catch (error) {
      console.error('❌ Error fetching websites:', error);
      setError('無法載入社別網站列表，請稍後再試。');
    }
    setLoading(false);
    console.log('✅ 社別網站列表載入完成');
  };

  useEffect(() => {
    fetchWebsites();
  }, []);

  // 選擇社別處理
  const handleWebsiteSelect = async (website) => {
    console.log('🎯 選擇社別:', website);
    setSelectedWebsite(website);
    setActiveTab('magazine');
    setIframeLoading(true);
    setIframeError(false);
  };

  // iframe 載入完成處理
  const handleIframeLoad = () => {
    console.log('✅ iframe 載入完成');
    setIframeLoading(false);
    setIframeError(false);
  };

  // iframe 載入錯誤處理
  const handleIframeError = () => {
    console.log('❌ iframe 載入失敗');
    setIframeLoading(false);
    setIframeError(true);
  };

  // 前往原網站
  const openOriginalSite = (url) => {
    console.log('🔗 開啟原網站:', url);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
        <p className="mt-2">載入社別網站中...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="text-center">全國社刊整合平台</h2>
          <p className="text-center text-muted">整合各社電子社刊，一站瀏覽全國{MEMBER_TERM}動態</p>
        </Col>
      </Row>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Row>
          <Col sm={3}>
            <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link eventKey="list" className="mb-2">
                  <i className="fas fa-list me-2"></i>社別列表
                </Nav.Link>
              </Nav.Item>
              {selectedWebsite && (
                <Nav.Item>
                  <Nav.Link eventKey="magazine" className="mb-2">
                    <i className="fas fa-book-open me-2"></i>
                    {selectedWebsite.store_name}社刊
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            {/* 社別快速選擇列表 */}
            <Card className="mt-3">
              <Card.Header>
                <h6 className="mb-0">快速選擇社別</h6>
              </Card.Header>
              <Card.Body className="p-2">
                <div className="d-grid gap-1">
                  {websites.map((website) => (
                    <Button
                      key={website.ss_id}
                      variant={selectedWebsite?.ss_id === website.ss_id ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => handleWebsiteSelect(website)}
                      className="text-start"
                    >
                      <small>{website.store_name}</small>
                    </Button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm={9}>
            <Tab.Content>
              {/* 社別列表頁面 */}
              <Tab.Pane eventKey="list">
                <Row>
                  {websites.map((website) => (
                    <Col md={6} lg={4} key={website.ss_id} className="mb-4">
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h5 className="card-title">{website.store_name}</h5>
                            <span className={`badge ${website.is_active ? 'bg-success' : 'bg-secondary'}`}>
                              {website.is_active ? '正常' : '維護中'}
                            </span>
                          </div>
                          <p className="card-text text-muted small">
                            網域: {website.subdomain}.imc.org.tw
                          </p>
                          <div className="d-grid gap-2">
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleWebsiteSelect(website)}
                              disabled={!website.is_active}
                            >
                              <i className="fas fa-book-open me-1"></i>
                              瀏覽社刊
                            </Button>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => openOriginalSite(website.website_url)}
                            >
                              <i className="fas fa-external-link-alt me-1"></i>
                              官方網站
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Tab.Pane>

              {/* 社刊瀏覽頁面 */}
              <Tab.Pane eventKey="magazine">
                {selectedWebsite && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h4>{selectedWebsite.store_name} - 電子社刊</h4>
                      <div>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-2"
                          onClick={fetchWebsites}
                        >
                          <i className="fas fa-sync-alt me-1"></i>
                          重新載入
                        </Button>
                        <Button 
                          variant="success" 
                          size="sm"
                          onClick={() => openOriginalSite(selectedWebsite.magazine_url)}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          前往原網站
                        </Button>
                      </div>
                    </div>

                    {/* iframe 載入指示器 */}
                    {iframeLoading && (
                      <div className="text-center mb-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>載入社刊內容中...</span>
                      </div>
                    )}

                    {/* 社刊內容區域 */}
                    {selectedWebsite.embed_type === 'external' ? (
                      /* 使用 Google Drive 的社別 - 直接跳轉 */
                      <div className="magazine-external-container" style={{ 
                        height: '800px', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '0.375rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <div className="text-center">
                          <i className="fas fa-file-pdf fa-5x text-primary mb-4"></i>
                          <h3>{selectedWebsite.store_name} 電子社刊</h3>
                          <p className="text-muted mb-4">
                            此社使用 Google Drive 托管社刊，<br/>
                            由於安全限制無法直接嵌入顯示。
                          </p>
                          <Button 
                            variant="primary" 
                            size="lg"
                            onClick={() => openOriginalSite(selectedWebsite.magazine_url)}
                          >
                            <i className="fas fa-external-link-alt me-2"></i>
                            前往 {selectedWebsite.store_name} 瀏覽社刊
                          </Button>
                          <div className="mt-3">
                            <small className="text-muted">
                              <i className="fas fa-info-circle me-1"></i>
                              點擊後將在新視窗開啟社刊頁面
                            </small>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* 一般社別 - iframe 嵌入 */
                      <div className="magazine-iframe-container" style={{ 
                        height: '800px', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '0.375rem',
                        overflow: 'hidden'
                      }}>
                        <iframe
                          src={selectedWebsite.magazine_url}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          title={`${selectedWebsite.store_name}社刊`}
                          onLoad={handleIframeLoad}
                          onError={handleIframeError}
                          style={{
                            display: iframeLoading || iframeError ? 'none' : 'block'
                          }}
                        />
                      
                      {/* 載入中提示 */}
                      <div 
                        className="d-flex align-items-center justify-content-center h-100 text-muted"
                        style={{
                          display: iframeLoading && !iframeError ? 'flex' : 'none'
                        }}
                      >
                        <div className="text-center">
                          <i className="fas fa-globe fa-3x mb-3"></i>
                          <p>正在載入 {selectedWebsite.store_name} 社刊...</p>
                          <small>如果載入時間過長，請嘗試直接前往原網站</small>
                        </div>
                      </div>

                      {/* 載入失敗提示 */}
                      <div 
                        className="d-flex align-items-center justify-content-center h-100 text-danger"
                        style={{
                          display: iframeError ? 'flex' : 'none'
                        }}
                      >
                        <div className="text-center">
                          <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
                          <h5>無法載入社刊內容</h5>
                          <p className="mb-3">
                            {selectedWebsite.store_name} 的社刊頁面可能設有存取限制，<br/>
                            或者網站暫時無法存取。
                          </p>
                          <Button 
                            variant="primary"
                            onClick={() => openOriginalSite(selectedWebsite.magazine_url)}
                          >
                            <i className="fas fa-external-link-alt me-2"></i>
                            前往 {selectedWebsite.store_name} 官方網站
                          </Button>
                        </div>
                      </div>
                      </div>
                    )}

                    {/* 說明文字 */}
                    <div className="mt-3">
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        此頁面整合顯示各社官方網站的電子社刊內容。如遇到顯示問題，請點選「前往原網站」按鈕。
                      </small>
                    </div>
                  </div>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
};

export default MagazineIntegration;